import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Radio,
  Crosshair,
  FileText,
  Settings,
  Radar,
  Bot,
  ShieldCheck,
  Users,
  Building2,
} from 'lucide-react';

export type TraderView =
  | 'dashboard'
  | 'live'
  | 'positions'
  | 'trades'
  | 'settings'
  | 'research'
  | 'streamrift'
  | 'g3'
  | 'g4'
  | 'g9';

export type ManagerView = 'manager-traders' | 'manager-org-account' | 'exports' | 'bots' | TraderView;

export type AdminView =
  | 'admin-manage'
  | 'admin-traders'
  | 'admin-g1-health'
  | 'manager-org-account'
  | 'exports'
  | 'bots'
  | TraderView;

export type AppView = TraderView;

/** A sub-page of a sidebar section, surfaced in that section's hover flyout. */
export interface NavTab {
  id: string;
  label: string;
  path: string;
  badge?: string;
}

export interface NavItem<V extends string = string> {
  id: V;
  /** Where clicking the icon goes. For tabbed sections this is the default (first) tab. */
  path: string;
  icon: LucideIcon;
  label: string;
  // 'trade' = always-available core trader nav; 'alpha' = the password-gated
  // dev/alpha testing zone (analytics + experimental, tested live on prod);
  // 'manage' = role-gated org/admin.
  section?: 'manage' | 'trade' | 'alpha';
  /** Optional small corner marker on the icon (e.g. 'test'). */
  badge?: string;
  /** When present, the icon shows a hover flyout listing these sub-pages. Clicking
   *  the icon opens `path` (the default tab); clicking a tab jumps straight to it. */
  tabs?: NavTab[];
}

export const TRADER_NAV_ITEMS: NavItem<TraderView>[] = [
  // Core trader surface — the product, always available.
  { id: 'dashboard', path: '/dashboard', icon: BarChart3, label: 'Value Dashboard', section: 'trade' },
  { id: 'positions', path: '/positions', icon: Crosshair, label: 'Position Tracker', section: 'trade' },
  { id: 'live', path: '/live', icon: Radio, label: 'Live Discovery', section: 'trade' },
  { id: 'trades', path: '/trades', icon: FileText, label: 'My Trades', section: 'trade' },
  { id: 'settings', path: '/settings', icon: Settings, label: 'Settings', section: 'trade' },
  // Research — analytics + the experimental lab, grouped under one icon (hover for tabs).
  {
    id: 'research',
    path: '/g3',
    icon: Radar,
    label: 'Research',
    section: 'alpha',
    tabs: [
      { id: 'g3', label: 'G3 — Analytics', path: '/g3' },
      { id: 'g4', label: 'G4 — Lab', path: '/g4' },
      { id: 'streamrift', label: 'StreamRift', path: '/streamrift' },
    ],
  },
];

// Items rendered in the footer (below the user name), in the listed order.
export const FOOTER_NAV_ORDER: readonly string[] = ['settings', 'manager-traders', 'admin-traders'];
export const FOOTER_NAV_IDS: ReadonlySet<string> = new Set(FOOTER_NAV_ORDER);

export const MANAGER_NAV_ITEMS: NavItem<ManagerView>[] = [
  { id: 'manager-traders', path: '/manager-traders', icon: Users, label: 'Org Settings', section: 'manage' },
  // Org Account — Org Account + Exports under one icon (keeps the admin org-selector
  // in place; hover for tabs).
  {
    id: 'manager-org-account',
    path: '/manager-org-account',
    icon: Building2,
    label: 'Org Account',
    section: 'manage',
    tabs: [
      { id: 'manager-org-account', label: 'Org Account', path: '/manager-org-account' },
      { id: 'exports', label: 'Exports', path: '/exports' },
    ],
  },
  ...TRADER_NAV_ITEMS,
  // Recycler (G10 edge control) sits at the end of the alpha/lab cluster.
  {
    id: 'bots',
    path: '/bots/vanilla',
    icon: Bot,
    label: 'Bots',
    section: 'alpha',
    badge: 'g10',
    tabs: [
      { id: 'vanilla', label: 'Vanilla MM', path: '/bots/vanilla' },
      { id: 'recycler', label: 'Recycler', path: '/bots/recycler' },
      { id: 'g9', label: 'G9 — Auto Assist', path: '/g9', badge: 'test' },
    ],
  },
];

export const ADMIN_NAV_ITEMS: NavItem<AdminView>[] = [
  // Management — Management + G1 Health under one icon.
  {
    id: 'admin-manage',
    path: '/admin-manage',
    icon: ShieldCheck,
    label: 'Management',
    section: 'manage',
    tabs: [
      { id: 'admin-manage', label: 'Management', path: '/admin-manage' },
      { id: 'admin-g1-health', label: 'G1 Health', path: '/admin-g1-health' },
    ],
  },
  { id: 'admin-traders', path: '/admin-traders', icon: Users, label: 'Org Settings', section: 'manage' },
  {
    id: 'manager-org-account',
    path: '/manager-org-account',
    icon: Building2,
    label: 'Org Account',
    section: 'manage',
    tabs: [
      { id: 'manager-org-account', label: 'Org Account', path: '/manager-org-account' },
      { id: 'exports', label: 'Exports', path: '/exports' },
    ],
  },
  ...TRADER_NAV_ITEMS,
  {
    id: 'bots',
    path: '/bots/vanilla',
    icon: Bot,
    label: 'Bots',
    section: 'alpha',
    badge: 'g10',
    tabs: [
      { id: 'vanilla', label: 'Vanilla MM', path: '/bots/vanilla' },
      { id: 'recycler', label: 'Recycler', path: '/bots/recycler' },
      { id: 'g9', label: 'G9 — Auto Assist', path: '/g9', badge: 'test' },
    ],
  },
];

export const NAV_ITEMS = TRADER_NAV_ITEMS;
